/**
 * The single API client. Attaches a fresh bearer token to every request and
 * normalises errors.
 */
import type { IPublicClientApplication } from '@azure/msal-browser';

import { apiRequest } from '../auth/msal.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller is authenticated but lacks the permission. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

let msalInstance: IPublicClientApplication | null = null;

export function setMsalInstance(instance: IPublicClientApplication): void {
  msalInstance = instance;
}

async function authHeader(): Promise<Record<string, string>> {
  if (!msalInstance) return {};
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return {};

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    // The ID token, not the access token — see the note in auth/msal.ts. The
    // access token here is issued for Microsoft Graph, not for this API, and
    // would fail audience validation.
    return { Authorization: `Bearer ${result.idToken}` };
  } catch {
    // Silent acquisition failing means the session needs refreshing. Send the
    // user through an interactive flow rather than firing an unauthenticated
    // request that would 401 confusingly.
    await msalInstance.acquireTokenRedirect({ ...apiRequest, account });
    return {};
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { ...(await authHeader()) };
  // A FormData body is sent as multipart, and the browser must set the
  // Content-Type itself — it carries the boundary.
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';

  // BASE_URL already carries a trailing slash ('/' or '/v2/'), so this yields
  // '/api/...' at root and '/v2/api/...' under a sub-path.
  const res = await fetch(`${import.meta.env.BASE_URL}api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  if (!res.ok) {
    let code = 'unknown';
    let message = res.statusText;
    try {
      const parsed = (await res.json()) as { error?: { code?: string; message?: string } };
      code = parsed.error?.code ?? code;
      message = parsed.error?.message ?? message;
    } catch {
      // Non-JSON error body — keep the status text.
    }
    throw new ApiError(res.status, code, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
